import { logger } from '../../services/logger';
import React, { useState } from 'react';
import { Market, MarketOption } from '../../types'; // Adjusted imports
import { toast } from 'react-hot-toast'; // Import toast for feedback
import { supabase } from '../../lib/db'; // Import supabase for DB operations
import { Loader2, AlertCircle, CheckCircle, XCircle, User, Info } from 'lucide-react'; // Import icons from lucide-react
import { useAuth } from '../../lib/auth'; // Import useAuth

// Define expected props
interface MarketResolverProps {
  market: Market; // Includes market_options and created_by
  marketId: string; // Make marketId required as it's used in logic
  onResolved?: () => void; // Callback when resolution is successful
}

// Define types for the data returned by the RPC functions
interface WinnerData {
  id: string; // Bet ID
  user_id: string;
  market_id: string;
  amount: number;
  amount_after_fee: number | null;
  market_option_id: number;
  status: string;
  user_email: string | null;
  user_metadata: { first_name?: string; last_name?: string } | null;
  option_name: string | null;
  winning_option_return: number | null;
  calculated_payout: number | null;
  // Add other fields returned by get_market_winners if needed
}

interface LoserData {
  id: string; // Bet ID
  user_id: string;
  market_id: string;
  amount: number;
  amount_after_fee: number | null; // Include if needed for losers display
  market_option_id: number;
  status: string;
  user_email: string | null;
  user_metadata: { first_name?: string; last_name?: string } | null;
  option_name: string | null;
   // Add other fields returned by get_market_losers if needed
}

// TODO: Implement market resolution logic

// Add a helper function to safely calculate potential payout
/* Removed as payout is calculated directly in JSX now
const calculatePotentialPayout = (bet: { amount?: number; odds?: number }): number => {
  // Safely extract amount and odds with fallbacks
  const amount = typeof bet.amount === 'number' ? bet.amount : 0;
  const odds = typeof bet.odds === 'number' ? bet.odds : 1;
  return amount * odds;
};
*/

export function MarketResolver({ market, marketId, onResolved }: MarketResolverProps) {
  const [selectedOptionId, setSelectedOptionId] = useState<number | null>(null); // State for single winner selection
  const [isProcessing, setIsProcessing] = useState<boolean>(false); // State for loading
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [winners, setWinners] = useState<WinnerData[]>([]);
  const [losers, setLosers] = useState<LoserData[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';

  // Resolution conditions.
  // `ended` = trading has finished but the scheduled status-update job hasn't
  // yet flipped the row to `expired`. We still allow resolution in that case.
  const isMarketActive =
    market.status === 'active' ||
    market.status === 'ended' ||
    market.status === 'expired';
  const isResolutionTimeSet = !!market.resolution_time;
  const isResolutionTimePassed = isResolutionTimeSet && new Date() >= new Date(market.resolution_time!);
  const isKnockoutMarket = market.market_type === 'knockout';

  // Determine if the current user can resolve this market
  let canResolve = false;
  let cannotResolveReason = "";
  if (!isMarketActive) {
      cannotResolveReason = `Market status is currently '${market.status}'.`;
  } else if (!isResolutionTimeSet) {
      cannotResolveReason = "Resolution time is not set for this market.";
  } else if (!isResolutionTimePassed && market.status === 'active') {
      cannotResolveReason = `Resolution time (${new Date(market.resolution_time!).toLocaleString()}) has not passed yet.`;
  } else {
      // Time and status are ok, check permissions
      if (market.is_private) {
          if (market.created_by === user?.id) {
              canResolve = true;
          } else {
              cannotResolveReason = "Only the creator can resolve this private market.";
          }
      } else { // Public market
          if (isAdmin) {
              canResolve = true;
          } else {
              cannotResolveReason = "Only admins can resolve public markets.";
          }
      }
  }

  // Filter out eliminated options for knockout markets
  const availableOptions = isKnockoutMarket
    ? market.market_options.filter(option => option.status !== 'eliminated')
    : market.market_options;

  const handleSelectOption = async (optionId: number) => {
    if (selectedOptionId === optionId) return;

    setSelectedOptionId(optionId);
    setShowConfirmation(false);
    setPreviewError(null);
    setIsPreviewLoading(true);

    try {
      // Call RPC function to get winners
      logger.info(`Calling get_market_winners with market_id: ${marketId}, winning_option_id: ${optionId}`);
      const { data: winnersData, error: winnersError } = await supabase
        .rpc('get_market_winners', {
          p_market_id: marketId,
          p_winning_option_id: optionId
        });

      if (winnersError) {
        logger.error('Error fetching market winners:', winnersError);
        throw new Error(`Failed to fetch winners: ${winnersError.message}`);
      }
      logger.info('Winners data:', winnersData);


      // Call RPC function to get losers
      logger.info(`Calling get_market_losers with market_id: ${marketId}, winning_option_id: ${optionId}`);
      const { data: losersData, error: losersError } = await supabase
        .rpc('get_market_losers', {
          p_market_id: marketId,
          p_winning_option_id: optionId
        });

      if (losersError) {
        logger.error('Error fetching market losers:', losersError);
        throw new Error(`Failed to fetch losers: ${losersError.message}`);
      }
      logger.info('Losers data:', losersData);

      // Directly set state with RPC data (assuming types match WinnerData/LoserData)
      setWinners(winnersData || []);
      setLosers(losersData || []);
      setShowConfirmation(true);

    } catch (error: unknown) { // Changed from any to unknown
      logger.error('Error fetching or processing bets for preview:', error);
      setPreviewError('Could not fetch bet details for preview.');
      setShowConfirmation(false);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleCancelConfirmation = () => {
    setSelectedOptionId(null);
    setShowConfirmation(false);
    setWinners([]);
    setLosers([]);
    setPreviewError(null);
  };

  const handleConfirmResolution = async () => {
    if (!canResolve || selectedOptionId === null) return;

    setIsProcessing(true);
    const winningOption = market.market_options.find(opt => opt.id === selectedOptionId);
    const winningOutcomeName = winningOption ? winningOption.name : 'Unknown';
    const toastId = toast.loading(`Resolving with winner: ${winningOutcomeName}...`);

    try {
      logger.info(`Calling resolve_market_by_option_id with market_id: ${marketId}, winning_option_id: ${selectedOptionId}`);
      const { data: resolveResult, error: resolveError } = await supabase
        .rpc('resolve_market_by_option_id', {
          p_market_id: marketId,
          p_winning_option_id: selectedOptionId,
          p_resolution_note: `Market resolved with winning option ID: ${selectedOptionId} (${winningOutcomeName})`
        });

      if (resolveError) {
        throw new Error(`Failed to resolve market: ${resolveError.message}`);
      }

      logger.info('Market resolution result:', resolveResult);
      toast.dismiss(toastId);
      toast.success(`Market resolved successfully! Winning option: ${winningOutcomeName}`);
      setShowConfirmation(false); // Hide confirmation on success
      setSelectedOptionId(null); // Reset selection
      onResolved?.();

    } catch (error) {
      toast.dismiss(toastId);
      logger.error('Error resolving market:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to resolve market.';
      toast.error(errorMessage);
      // Keep confirmation open on error? Or close? For now, let's close it.
      // setShowConfirmation(false); 
    } finally {
      setIsProcessing(false);
    }
  };

  const getDisplayName = (betUser: { user_email: string | null; user_metadata: { first_name?: string; last_name?: string; } | null }) => {
    const meta = betUser.user_metadata;
    if (meta?.first_name) {
      return `${meta.first_name} ${meta.last_name || ''}`.trim();
    }
    // Use optional chaining and nullish coalescing for safety
    const email = betUser.user_email ?? '';
    return email.split('@')[0] || 'Unknown User'; // Handle empty email
  };

  // Calculate totals for display
  const totalWinnersBetAfterFee = winners.reduce((sum, winner) => sum + (winner.amount_after_fee ?? 0), 0);
  const totalWinnersPayout = winners.reduce((sum, winner) => sum + (winner.calculated_payout ?? 0), 0);
  const totalLosersBetAfterFee = losers.reduce((sum, loser) => sum + (loser.amount_after_fee ?? 0), 0);
  
  // Display knockout market type info if relevant
  const isKnockoutWithEliminatedOptions = isKnockoutMarket && 
    market.market_options.some(option => option.status === 'eliminated');
  
  return (
    <div className="p-6 bg-white rounded-lg shadow">
      {/* <h2 className="text-xl font-semibold mb-4 text-gray-900">Resolve Market (ID: {marketId})</h2> */}
      {/* <p className="text-gray-600 mb-6">Select the winning outcome for "{market.title}".</p> */}
      
      {/* Knockout market info alert */}
      {isKnockoutWithEliminatedOptions && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-sm flex items-center gap-2">
          <Info className="w-5 h-5 flex-shrink-0" />
          <p>This is a knockout market. Eliminated options are not shown as they cannot be selected as winners.</p>
        </div>
      )}
      
      {/* Add logic to display options and select winner(s) based on market type */}
      <div className={`space-y-2 ${!canResolve ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <p className="font-medium">Select Winning Outcome:</p>
        {availableOptions.length > 0 ? (
          availableOptions.map((option: MarketOption) => (
            <label 
              key={option.id} 
              className={`flex items-center space-x-2 p-2 border rounded-md ${
                selectedOptionId === option.id ? 'bg-indigo-50 border-indigo-300' : 'border-gray-200'
              } ${canResolve ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-not-allowed'}`}
            >
              <input
                type='radio'
                name="winningOutcome"
                value={option.id}
                checked={selectedOptionId === option.id}
                onChange={() => handleSelectOption(option.id)}
                disabled={!canResolve || isPreviewLoading || isProcessing}
                className="form-radio h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500 disabled:opacity-70 disabled:cursor-not-allowed"
              />
              <span>{option.name}</span>
              {option.status !== 'active' && (
                <span className="ml-2 text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                  {option.status}
                </span>
              )}
            </label>
          ))
        ) : (
          <p className="text-gray-500">No available options for this market.</p>
        )}
      </div>
      
      {/* Display reason if cannot resolve */} 
      {!canResolve && cannotResolveReason && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-yellow-800 text-sm flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>Cannot resolve market: {cannotResolveReason}</span>
        </div>
      )}

      {/* Preview Loading State */}
      {isPreviewLoading && (
         <div className="mt-4 flex items-center justify-center text-gray-500">
           <Loader2 className="animate-spin mr-2 h-4 w-4" />
           Loading bet details...
         </div>
      )}

      {/* Preview Error */}
       {previewError && (
         <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm flex items-center gap-2">
             <AlertCircle className="w-5 h-5 flex-shrink-0" />
             <span>{previewError}</span>
         </div>
       )}

      {/* Confirmation Section */}
      {showConfirmation && selectedOptionId !== null && (
        <div className="mt-6 p-4 border border-gray-200 rounded-md bg-gray-50">
          <h3 className="text-lg font-medium mb-2">Resolution Preview</h3>
          <p className="text-gray-600 mb-4">
            Winning Option: <span className="font-medium">{market.market_options.find(opt => opt.id === selectedOptionId)?.name || 'Unknown'}</span>
            {(winners.length > 0 && winners[0].winning_option_return !== null) && (
              <span className="ml-2 text-sm text-gray-500">(Return: {winners[0].winning_option_return.toFixed(2)})</span>
            )}
          </p>
          
          {/* Winners */}
          <div className="mb-4">
            <h4 className="font-medium text-green-700 flex items-center mb-2">
              <CheckCircle className="w-4 h-4 mr-1" /> 
              Winners ({winners.length})
            </h4>
            {winners.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trade (After Fee)</th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {winners.map(winner => (
                      <tr key={winner.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-1" />
                          {getDisplayName(winner)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{(winner.amount_after_fee ?? winner.amount ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                          {winner.calculated_payout !== null ? 
                           winner.calculated_payout.toFixed(2) : 
                           'N/A'
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                   {/* Winners Footer */} 
                   <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                     <tr>
                       <td className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Totals:</td>
                       <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{totalWinnersBetAfterFee.toFixed(2)}</td>
                       <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{totalWinnersPayout.toFixed(2)}</td>
                     </tr>
                   </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 p-2 bg-white border border-gray-200 rounded-md">No winning trades found.</p>
            )}
          </div>
          
          {/* Losers */}
          <div>
            <h4 className="font-medium text-red-700 flex items-center mb-2">
              <XCircle className="w-4 h-4 mr-1" /> 
              Losers ({losers.length})
            </h4>
            {losers.length > 0 ? (
              <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trade (After Fee)</th>
                      <th scope="col" className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payout</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {losers.map(loser => (
                      <tr key={loser.id}>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 flex items-center">
                          <User className="w-4 h-4 text-gray-400 mr-1" />
                          {getDisplayName(loser)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">{(loser.amount_after_fee ?? loser.amount ?? 0).toFixed(2)}</td>
                        <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">0.00</td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Losers Footer */}
                   <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                     <tr>
                       <td className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Totals:</td>
                       <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">{totalLosersBetAfterFee.toFixed(2)}</td>
                       <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-gray-900">0.00</td>
                     </tr>
                   </tfoot>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500 p-2 bg-white border border-gray-200 rounded-md">No losing trades found.</p>
            )}
          </div>
          
          {/* Warning */}
          <div className="mt-4 flex items-center gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-800">
            <Info className="w-5 h-5 flex-shrink-0" />
            <p>This action is irreversible. All market outcomes will be finalized and payouts will be processed automatically.</p>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-4 flex justify-end gap-3">
            <button
              onClick={handleCancelConfirmation}
              disabled={isProcessing}
              className="px-4 py-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmResolution}
              disabled={!canResolve || isProcessing}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isProcessing ? 'Processing...' : 'Confirm Resolution'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
} 